<template>
  <form @submit.prevent="handleSave">
    <h2>{{ t('companyData') }}</h2>
    <div class="mt-4 grid grid-cols-3 gap-4">
      <InputField name="name" :label="t('name')" class="col-span-2" />
      <SelectField
        name="isActive"
        :label="t('status')"
        :options="activeOptions"
        :disabled="!canUpdate"
      />
      <InputField name="street" :label="t('street')" class="col-start-1" />
      <InputField name="zip" :label="t('zip')" />
      <SearchableSelectField
        name="countryCode"
        :label="t('country')"
        :options="countryOptions"
        :track-by="['label', 'labelEn', 'labelShort', 'value', 'alternativeNames']"
      />
      <InputField name="city.de" :label="t('cityDe')" class="col-start-1" />
      <InputField name="city.en" :label="t('cityEn')" />
      <InputField name="city.es" :label="t('cityEs')" />
      <InputField name="email" :label="t('email')" class="col-start-1" />
      <InputField name="phone" :label="t('phone')" />
      <InputField name="website" :label="t('website')" />
      <InputField name="vatId" :label="t('vatId')" class="col-start-1" />
      <InputField name="taxNumber" :label="t('taxNumber')" empty-value-to="null" />
      <InputField name="commercialRegister" :label="t('commercialRegister')" />
      <InputField
        name="managingDirectors"
        :label="t('managingDirectors')"
        class="col-span-2 col-start-1"
      />
    </div>

    <Divider />

    <h2>{{ t('payments') }}</h2>
    <div class="mt-4 grid grid-cols-3 gap-4">
      <InputField name="bankName" :label="t('bankName')" />
      <InputField name="iban" :label="t('iban')" />
      <InputField name="bic" :label="t('bic')" />
      <div class="col-start-1">
        <InputField name="sepaCreditorId" :label="t('sepaCreditorId')" empty-value-to="null" />
        <p class="mt-1 text-xs text-obsidian-500">{{ t('sepaCreditorIdHint') }}</p>
      </div>
      <InputField name="adyenMerchantAccount" :label="t('adyenMerchantAccount')" />
      <div>
        <InputField
          name="invoiceNumberPrefix"
          :label="t('invoiceNumberPrefix')"
          :disabled="!!legalEntity && hasIssuedInvoices"
        />
        <p class="mt-1 text-xs text-obsidian-500">{{ t('invoiceNumberPrefixHint') }}</p>
      </div>
      <div class="col-span-3 grid grid-cols-3 gap-x-4">
        <SelectField name="isOssRegistered" :label="t('ossRegistration')" :options="ossOptions" />
        <p class="col-span-3 mt-1 text-xs text-obsidian-500">{{ t('ossRegistrationHint') }}</p>
      </div>
    </div>

    <Divider />

    <h2>{{ t('terms') }}</h2>
    <div class="mt-4 grid grid-cols-3 gap-4">
      <InputField name="localLanguage" :label="t('localLanguage')" />
    </div>
    <div class="mt-4 flex flex-col gap-4">
      <div>
        <p class="mb-1 text-sm font-medium">
          {{ t('localTerms', { language: formValues.localLanguage || '–' }) }}
        </p>
        <RichTextEditor v-model="localTerms" />
        <InputHint :hint="localTermsError && t('termsRequired')" is-error />
      </div>
      <div>
        <p class="mb-1 text-sm font-medium">{{ t('englishTerms') }}</p>
        <RichTextEditor v-model="englishTerms" />
        <InputHint :hint="englishTermsError && t('termsRequired')" is-error />
      </div>
    </div>

    <Divider />

    <div class="mt-3 flex justify-end">
      <CVButton :is-loading="isLoading" :disabled="!canUpdate" @click="handleSave">
        {{ t('save') }}
      </CVButton>
    </div>
  </form>
</template>

<script lang="ts" setup>
import { computed, ref } from 'vue';
import { cloneDeep } from 'lodash';
import { useI18n } from 'vue-i18n';
import { useField, useForm } from 'vee-validate';
import type { LegalEntity, UpdateLegalEntityDto } from '@carvia/ros-client-types';
import { subject } from '@casl/ability';
import { useAbility } from '@casl/vue';
import CVButton from '@/components/buttons/CVButton.vue';
import Divider from '@/components/Divider.vue';
import InputField from '@/components/InputField.vue';
import InputHint from '@/components/InputHint.vue';
import RichTextEditor from '@/components/RichTextEditor.vue';
import SearchableSelectField from '@/components/SearchableSelectField.vue';
import SelectField from '@/components/SelectField.vue';
import { usePreventLeavingUnsavedForm } from '@/hooks/use-prevent-leaving-unsaved-form';
import { formHasChanged } from '@/hooks/use-form-has-changed';
import { getCountries } from '@/utils/countries';
import { legalEntitySchema } from '@/validation/legal-entity.schema';

const props = defineProps<{
  legalEntity?: LegalEntity;
  isLoading: boolean;
  /** Prefix changes are blocked once the legal entity has issued invoices. */
  hasIssuedInvoices: boolean;
}>();

const emit = defineEmits<{
  (e: 'submit', value: UpdateLegalEntityDto): void;
}>();

const { t, locale } = useI18n();
const ability = useAbility();

const canUpdate = computed(() =>
  props.legalEntity
    ? ability.can('update', subject('LegalEntity', props.legalEntity))
    : ability.can('create', 'LegalEntity'),
);

const countryOptions = computed(() => getCountries(locale.value));
const activeOptions = computed(() => [
  { label: t('active'), value: 'true' },
  { label: t('inactive'), value: 'false' },
]);
const ossOptions = computed(() => [
  { label: t('ossRegistered'), value: 'true' },
  { label: t('ossNotRegistered'), value: 'false' },
]);

const initialValues = {
  name: props.legalEntity?.name ?? '',
  street: props.legalEntity?.street ?? '',
  zip: props.legalEntity?.zip ?? '',
  city: {
    de: props.legalEntity?.city.de ?? '',
    en: props.legalEntity?.city.en ?? '',
    es: props.legalEntity?.city.es ?? '',
  },
  countryCode: props.legalEntity?.countryCode ?? 'DE',
  localLanguage: props.legalEntity?.localLanguage ?? 'de',
  email: props.legalEntity?.email ?? '',
  phone: props.legalEntity?.phone ?? '',
  website: props.legalEntity?.website ?? '',
  vatId: props.legalEntity?.vatId ?? '',
  taxNumber: props.legalEntity?.taxNumber ?? null,
  commercialRegister: props.legalEntity?.commercialRegister ?? '',
  managingDirectors: props.legalEntity?.managingDirectors ?? '',
  iban: props.legalEntity?.iban ?? '',
  bic: props.legalEntity?.bic ?? '',
  bankName: props.legalEntity?.bankName ?? '',
  sepaCreditorId: props.legalEntity?.sepaCreditorId ?? null,
  adyenMerchantAccount: props.legalEntity?.adyenMerchantAccount ?? '',
  invoiceNumberPrefix: props.legalEntity?.invoiceNumberPrefix ?? '',
  localTerms: props.legalEntity?.localTerms ?? '',
  englishTerms: props.legalEntity?.englishTerms ?? '',
  isActive: props.legalEntity?.isActive === false ? 'false' : 'true',
  isOssRegistered: props.legalEntity?.isOssRegistered ? 'true' : 'false',
};

const { handleSubmit, values: formValues } = useForm({
  initialValues,
  validationSchema: legalEntitySchema,
});

const { value: localTerms, errorMessage: localTermsError } = useField<string>('localTerms');
const { value: englishTerms, errorMessage: englishTermsError } = useField<string>('englishTerms');

const savedValues = ref(cloneDeep(initialValues));
usePreventLeavingUnsavedForm(() => formHasChanged(savedValues.value, formValues));

const handleSave = handleSubmit((values) => {
  savedValues.value = cloneDeep(formValues);
  emit('submit', {
    ...values,
    isActive: String(values.isActive) === 'true',
    isOssRegistered: String(values.isOssRegistered) === 'true',
  } as UpdateLegalEntityDto);
});
</script>

<i18n lang="json">
{
  "en": {
    "companyData": "Company data",
    "name": "Company name incl. legal form",
    "status": "Status",
    "active": "Active",
    "inactive": "Inactive",
    "street": "Street",
    "zip": "Zip",
    "cityDe": "City (German)",
    "cityEn": "City (English)",
    "cityEs": "City (Spanish)",
    "country": "Country",
    "email": "Email",
    "phone": "Phone",
    "website": "Website",
    "vatId": "VAT ID",
    "taxNumber": "Tax number",
    "commercialRegister": "Commercial register number",
    "managingDirectors": "Managing directors",
    "payments": "Bank, payments and invoices",
    "bankName": "Bank",
    "iban": "IBAN",
    "bic": "BIC",
    "sepaCreditorId": "SEPA creditor ID",
    "sepaCreditorIdHint": "Without a SEPA creditor ID the legal entity cannot collect SEPA direct debits.",
    "termsRequired": "Required",
    "adyenMerchantAccount": "Adyen merchant account",
    "invoiceNumberPrefix": "Invoice number prefix",
    "invoiceNumberPrefixHint": "Leads every invoice number of this legal entity and must differ from all other legal entities. It cannot change once the first invoice has been issued.",
    "ossRegistration": "EU One-Stop Shop (OSS)",
    "ossRegistered": "Registered",
    "ossNotRegistered": "Not registered",
    "ossRegistrationHint": "With an EU One-Stop Shop (OSS) registration, bookings are taxed at the VAT rate of the country of supply: the handover country, or the customer's country for rentals over 30 days. Without it, bookings are taxed at the VAT rate of the legal entity's country. Either way, business customers with a VAT ID get reverse charge (EU) or export (outside the EU) suggested when the country of supply is abroad.",
    "terms": "Terms and conditions",
    "localLanguage": "Local language (ISO code, e.g. de, fr)",
    "localTerms": "Terms in the local language ({language})",
    "englishTerms": "Terms in English",
    "save": "Save"
  },
  "de": {
    "companyData": "Firmendaten",
    "name": "Firmenname inkl. Rechtsform",
    "status": "Status",
    "active": "Aktiv",
    "inactive": "Inaktiv",
    "street": "Straße",
    "zip": "PLZ",
    "cityDe": "Stadt (Deutsch)",
    "cityEn": "Stadt (Englisch)",
    "cityEs": "Stadt (Spanisch)",
    "country": "Land",
    "email": "E-Mail",
    "phone": "Telefon",
    "website": "Website",
    "vatId": "USt.-ID",
    "taxNumber": "Steuernummer",
    "commercialRegister": "Handelsregisternummer",
    "managingDirectors": "Geschäftsführer",
    "payments": "Bank, Zahlungen und Rechnungen",
    "bankName": "Bank",
    "iban": "IBAN",
    "bic": "BIC",
    "sepaCreditorId": "SEPA-Gläubiger-ID",
    "sepaCreditorIdHint": "Ohne SEPA-Gläubiger-ID kann die Gesellschaft keine SEPA-Lastschriften einziehen.",
    "termsRequired": "Pflichtfeld",
    "adyenMerchantAccount": "Adyen Merchant Account",
    "invoiceNumberPrefix": "Rechnungsnummern-Präfix",
    "invoiceNumberPrefixHint": "Steht vor jeder Rechnungsnummer dieser Gesellschaft und muss sich von allen anderen Gesellschaften unterscheiden. Nach der ersten Rechnung ist es nicht mehr änderbar.",
    "ossRegistration": "EU-One-Stop-Shop (OSS)",
    "ossRegistered": "Registriert",
    "ossNotRegistered": "Nicht registriert",
    "ossRegistrationHint": "Mit Registrierung im EU-One-Stop-Shop (OSS) gilt die Mehrwertsteuer des Leistungslands: das Land der Übergabe, bei Mieten über 30 Tage das Land des Kunden. Ohne Registrierung gilt die Mehrwertsteuer des Sitzlands der Gesellschaft. In beiden Fällen wird Geschäftskunden mit USt-IdNr. Reverse Charge (EU) oder Export (außerhalb der EU) vorgeschlagen, wenn das Leistungsland im Ausland liegt.",
    "terms": "AGB",
    "localTerms": "AGB in Landessprache ({language})",
    "localLanguage": "Landessprache (ISO-Code, z. B. de, fr)",
    "englishTerms": "AGB auf Englisch",
    "save": "Speichern"
  },
  "es": {
    "companyData": "Datos de la empresa",
    "name": "Nombre de la empresa con forma jurídica",
    "status": "Estado",
    "active": "Activa",
    "inactive": "Inactiva",
    "street": "Calle",
    "zip": "Código postal",
    "cityDe": "Ciudad (alemán)",
    "cityEn": "Ciudad (inglés)",
    "cityEs": "Ciudad (español)",
    "country": "País",
    "email": "Correo electrónico",
    "phone": "Teléfono",
    "website": "Sitio web",
    "vatId": "NIF-IVA",
    "taxNumber": "Número fiscal",
    "commercialRegister": "Número de registro mercantil",
    "managingDirectors": "Directores generales",
    "payments": "Banco, pagos y facturas",
    "bankName": "Banco",
    "iban": "IBAN",
    "bic": "BIC",
    "sepaCreditorId": "ID de acreedor SEPA",
    "sepaCreditorIdHint": "Sin ID de acreedor SEPA la sociedad no puede cobrar adeudos directos SEPA.",
    "termsRequired": "Obligatorio",
    "adyenMerchantAccount": "Cuenta de comercio de Adyen",
    "invoiceNumberPrefix": "Prefijo del número de factura",
    "invoiceNumberPrefixHint": "Precede a cada número de factura de esta sociedad y debe ser distinto al de las demás sociedades. No se puede cambiar después de la primera factura.",
    "ossRegistration": "Ventanilla única de la UE (OSS)",
    "ossRegistered": "Registrada",
    "ossNotRegistered": "No registrada",
    "ossRegistrationHint": "Con registro en la ventanilla única de la UE (OSS), las reservas tributan al tipo de IVA del país de prestación: el país de entrega o, en alquileres de más de 30 días, el país del cliente. Sin registro, tributan al tipo de IVA del país de la sociedad. En ambos casos, a los clientes empresariales con NIF-IVA se les propone inversión del sujeto pasivo (UE) o exportación (fuera de la UE) cuando el país de prestación está en el extranjero.",
    "terms": "Condiciones generales",
    "localLanguage": "Idioma local (código ISO, p. ej. de, fr)",
    "localTerms": "Condiciones en el idioma local ({language})",
    "englishTerms": "Condiciones en inglés",
    "save": "Guardar"
  }
}
</i18n>
